import InputIconSearchPanelMultiSelection from "./InputIconSearchPanelMultiSelection";
import DataTable from "react-data-table-component";
import ButtonIconH from "./ButtonIconH";
import { SelectedItemsContainer } from "./SelectedItemsContainer";
import { OpticalSalesContainer } from "./OpticalSalesContainer";
import moment from "moment";
import "moment/locale/es";
import { useEffect, useRef, useState } from "react";
import { userData, inputOnEnter, focusElement } from "../js/utils";
import { post } from "../js/post";
import { dashboardSalesSummaryTable, sortFunction } from "../js/tableColumns";
import Dropdown from "./Dropdown";
import { SalesExportPopup } from "./SalesExportPopup";
import { useAgents } from "../hooks/useAgents";
import { useOptics } from "../hooks/useOptics";
import { useCampaigns } from "../hooks/useCampaigns";
import DateInput from "./DateInput";

export function DashboardSalesPanelSalesView({
  createPopupQuestion,
  removePopupQuestion,
  presetFilter,
  onPresetLoaded,
  active,
}) {
  const opticalList = useOptics();
  const [selectedOpticalList, setSelectedOpticalList] = useState([]);
  const [salesStartDate, setSalesStartDate] = useState(
    moment().format("YYYY-MM-DD"),
  );
  const [salesEndDate, setSalesEndDate] = useState(
    moment().format("YYYY-MM-DD"),
  );
  const campaignList = [
    { id: "", text: "" },
    ...useCampaigns({ incluirCampanasCRMSoloActivas: false }),
  ];
  const [selectedCampaign, setSelectedCampaign] = useState({
    id: "",
    text: "",
  });
  const agentList = useAgents().map((e) => e.text);
  const [selectedAgentList, setSelectedAgentList] = useState([]);
  const [salesList, setSalesList] = useState([]);
  const [salesSummaryData, setSalesSummaryData] = useState([]);
  const [exportPopup, setExportPopup] = useState(false);
  const appliedPresetKey = useRef(null);
  const presetPending = useRef(false);
  const presetLoaded = useRef(false);
  const defaultsApplied = useRef(false);
  const lastRequestId = useRef(0);

  async function getDashboardSales() {
    const fromPreset = presetPending.current;
    presetPending.current = false;
    const requestId = ++lastRequestId.current;
    const optics = opticalList
      .filter((op) => selectedOpticalList.includes(op.text))
      .map((op) => op.id);

    const response = await post(
      "support-extra/dashboard/ObtenerVentasDashboard.php",
      {
        token: userData.token,
        opticas: optics,
        fechaDesde: salesStartDate,
        fechaHasta: salesEndDate,
        agentes: selectedAgentList,
        campanas: selectedCampaign.text !== "" ? [selectedCampaign.text] : [],
      },
    );
    // Descartar respuestas de peticiones ya superadas por otra más reciente
    if (requestId !== lastRequestId.current) return;
    if (fromPreset) presetLoaded.current = true;
    setSalesList(
      [...response].sort((a, b) => sortFunction(a.idOptica, b.idOptica)),
    );
  }

  async function getMoreSales(idOptica, from, total) {
    const response = await post(
      "support-extra/dashboard/ObtenerVentasDashboardVerMas.php",
      {
        token: userData.token,
        idOptica,
        fechaDesde: salesStartDate,
        fechaHasta: salesEndDate,
        agentes: selectedAgentList,
        campanas: selectedCampaign.text !== "" ? [selectedCampaign.text] : [],
        start: from,
        total,
      },
    );

    const listAux = salesList.map((e) =>
      e.idOptica === idOptica
        ? {
            ...e,
            ventas: [...e.ventas, ...response.ventas],
          }
        : e,
    );
    setSalesList(listAux);
  }

  function resetFilters() {
    setSelectedOpticalList([]);
    setSelectedAgentList([]);
    setSelectedCampaign({ id: "", text: "" });
    setSalesStartDate("");
    setSalesEndDate("");
  }

  function openReportPopup() {
    if (selectedOpticalList.length > 0) setExportPopup(true);
    else {
      createPopupQuestion(
        "Seleccione al menos una óptica.",
        () => {
          removePopupQuestion();
          return;
        },
        null,
        "Entendido",
      );
    }
  }

  useEffect(() => {
    if (active) focusElement(1);
  }, [active]);

  // Por defecto, todas las ópticas seleccionadas en cuanto se cargan
  useEffect(() => {
    if (defaultsApplied.current || opticalList.length === 0) return;
    defaultsApplied.current = true;
    setSelectedOpticalList(opticalList.map((op) => op.text));
  }, [opticalList]);

  // Filtro aplicado desde "Listar ventas" en Análisis de ventas
  useEffect(() => {
    if (!presetFilter || appliedPresetKey.current === presetFilter.key) return;
    const optic = opticalList.find(
      (op) => String(op.id) === String(presetFilter.idOptica),
    );
    if (!optic) return;
    appliedPresetKey.current = presetFilter.key;
    presetPending.current = true;
    setSelectedOpticalList([optic.text]);
    setSalesStartDate(presetFilter.date);
    setSalesEndDate(presetFilter.date);
  }, [presetFilter, opticalList]);

  useEffect(() => {
    if (!defaultsApplied.current) return;
    getDashboardSales();
  }, [
    selectedOpticalList,
    selectedAgentList,
    selectedCampaign,
    salesStartDate,
    salesEndDate,
  ]);

  useEffect(() => {
    const summary = {
      operaciones: salesList.reduce((acc, e) => acc + e.ventas.length, 0),
      base: salesList.reduce(
        (acc, e) =>
          acc + e.ventas.reduce((sum, venta) => sum + (venta.base ?? 0), 0),
        0,
      ),
      iva: salesList.reduce(
        (acc, e) =>
          acc + e.ventas.reduce((sum, venta) => sum + (venta.iva ?? 0), 0),
        0,
      ),
      total: salesList.reduce(
        (acc, e) =>
          acc + e.ventas.reduce((sum, venta) => sum + (venta.total ?? 0), 0),
        0,
      ),
    };

    setSalesSummaryData([summary]);

    // Avisar tras pintar los datos pedidos desde "Listar ventas"
    if (presetLoaded.current) {
      presetLoaded.current = false;
      onPresetLoaded?.();
    }
  }, [salesList]);

  return (
    <div className="dashboard-sales-filter">
      <SalesExportPopup
        show={exportPopup}
        onClose={() => setExportPopup(false)}
        opticalList={opticalList
          .filter((e) => selectedOpticalList.includes(e.text))
          .map((e) => e.id)}
        opticalNames={selectedOpticalList}
        agentList={selectedAgentList}
        campaign={selectedCampaign.text}
        startDate={salesStartDate}
        endDate={salesEndDate}
        createPopupQuestion={createPopupQuestion}
        removePopupQuestion={removePopupQuestion}
      />
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 10,
          width: "100%",
        }}
      >
        <div className="opt-table-content grid-10" id="sales-dashboard-grid">
          <div className="cell cell-start-content data-field">
            <span className="label">Ópticas</span>
          </div>
          <div className="cell">
            <InputIconSearchPanelMultiSelection
              size={0}
              tabIndex={1}
              type={"type-alphanum-ext"}
              options={opticalList.map((e) => e.text)}
              selectedOptions={selectedOpticalList}
              onChange={(e) => {
                setSelectedOpticalList(e);
              }}
            ></InputIconSearchPanelMultiSelection>
          </div>
          <div className="cell"></div>
          <div className="cell cell-start-content data-field">
            <span className="label">Fecha desde</span>
          </div>
          <div className="cell  cell-start-content">
            <DateInput
              max="9999-12-31"
              tabIndex={4}
              className="input-field input-cell w-100"
              value={salesStartDate}
              onChange={(e) => {
                const value = e.target.value;
                setSalesStartDate(
                  value ? moment(value).format("YYYY-MM-DD") : "",
                );
              }}
              onKeyUp={(e) => inputOnEnter(e)}
            />
          </div>
          <div className="cell"></div>
          <div className="cell cell-start-content data-field">
            <span className="label">Fecha hasta</span>
          </div>
          <div className="cell  cell-start-content">
            <DateInput
              max="9999-12-31"
              tabIndex={5}
              className="input-field input-cell w-100"
              value={salesEndDate}
              onChange={(e) => {
                const value = e.target.value;
                setSalesEndDate(
                  value ? moment(value).format("YYYY-MM-DD") : "",
                );
              }}
              onKeyUp={(e) => inputOnEnter(e)}
            />
          </div>
          <div className="cell"></div>
          <div className="cell">
            <button className="cell-label" onClick={resetFilters} tabIndex={-1}>
              Limpiar filtros
            </button>
          </div>
          {selectedOpticalList.length > 0 && (
            <div className="cell combined-cells-10 cell-start-content">
              <SelectedItemsContainer
                nameToShow="Ópticas seleccionadas:"
                items={selectedOpticalList}
                onClick={(e) => {
                  setSelectedOpticalList((prev) =>
                    prev.filter((op) => op !== e),
                  );
                }}
              ></SelectedItemsContainer>
            </div>
          )}
          <div className="cell cell-start-content data-field">
            <span className="label">Agentes</span>
          </div>
          <div className="cell">
            <InputIconSearchPanelMultiSelection
              size={0}
              tabIndex={2}
              type={"type-alphanum-ext"}
              options={agentList}
              selectedOptions={selectedAgentList}
              onChange={(e) => {
                setSelectedAgentList(e);
              }}
            ></InputIconSearchPanelMultiSelection>
          </div>
          <div className="cell combined-cells-8"></div>
          {selectedAgentList.length > 0 && (
            <div className="cell combined-cells-10 cell-start-content">
              <SelectedItemsContainer
                nameToShow="Agentes seleccionados:"
                items={selectedAgentList}
                onClick={(e) => {
                  setSelectedAgentList((prev) =>
                    prev.filter((optic) => optic !== e),
                  );
                }}
              ></SelectedItemsContainer>
            </div>
          )}
          <div className="cell cell-start-content data-field">
            <span className="label">Campaña</span>
          </div>
          <div className="cell">
            <Dropdown
              tabIndex={3}
              optionList={campaignList}
              size={0}
              op={selectedCampaign.text}
              mandatory={false}
              onChange={(e) => {
                setSelectedCampaign({
                  id: e.id,
                  text: e.text,
                });
              }}
            ></Dropdown>
          </div>
          <div className="cell combined-cells-8"></div>
          <div className="cell combined-cells-10" style={{ marginTop: 10 }}>
            <ButtonIconH
              text="Generar Informes"
              highlight={true}
              onClick={openReportPopup}
            ></ButtonIconH>
          </div>
        </div>
        {salesList.map((e, i) => {
          return (
            <OpticalSalesContainer
              optical={e}
              key={i}
              onLoadMore={async (id, from) => {
                await getMoreSales(id, from, 20);
              }}
            ></OpticalSalesContainer>
          );
        })}
        {salesList.length > 0 && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 10,
              borderTop: "1px solid #d6d6d6",
              paddingTop: 10,
            }}
          >
            <span className="dashboard-section-or-title">
              RESUMEN DE VENTAS
            </span>
            <DataTable
              columns={dashboardSalesSummaryTable}
              data={salesSummaryData}
              className="custom-datatable dashboard-custom-table non-interactive summary-row"
            />
          </div>
        )}
      </div>
    </div>
  );
}
